// src/lib/resources/api/client.ts
// Safe in 'use client' components. No server imports here.

/**
 * @fileoverview Client-side API functions for fetching resource data with robust error handling.
 * @description This module provides enhanced API client functions that gracefully handle common
 * error scenarios in dynamic content systems, particularly missing resources and server errors.
 * 
 * Key improvements over basic fetch:
 * - 404 responses return null instead of throwing errors (missing resources are expected)
 * - HTML error pages are detected and handled gracefully (prevents JSON parse errors)
 * - Meaningful error messages for debugging while maintaining good UX
 * - Defensive programming with multiple layers of error handling
 * 
 * @module resources/api/client
 * @see {@link fetchResourceByReferenceClient} - Main function for fetching related resources
 * @see {@link getJSON} - Enhanced JSON fetch utility with error handling
 */

import { clientLogger } from '~/lib/logger-client'
import type { ResourceInstance } from '~/lib/resources/types'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'
import type { RelatedResource } from '~/lib/resources/types/shared/base'
import type { ResourceType } from '~/lib/resources/types/shared/enums'

/**
 * Enhanced JSON fetch utility with robust error handling for API responses.
 * 
 * This function addresses common issues with API error handling:
 * 1. Missing resources (404s) are expected in dynamic content systems
 * 2. Server error pages often return HTML instead of JSON
 * 3. Error responses may not be readable text
 * 
 * @param url - The API endpoint to fetch JSON from
 * @returns Promise<T> - Parsed JSON data, or null for 404s
 * @throws Error - For non-404 HTTP errors with meaningful error messages
 * 
 * @example
 * // Handles missing resources gracefully
 * const resource = await getJSON<ResourceInstance>('/api/resources/123')
 * if (resource === null) {
 *   // Resource doesn't exist - handle gracefully
 * }
 * 
 * @example
 * // Throws meaningful errors for server issues
 * try {
 *   const data = await getJSON<Data>('/api/data')
 * } catch (error) {
 *   // Error contains meaningful message, not HTML
 * }
 */
async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'force-cache' })
  
  if (!res.ok) {
    // Handle 404s gracefully - missing resources are expected in dynamic systems
    // This prevents cascading failures when related resources don't exist
    if (res.status === 404) {
      return null as T
    }
    
    // For other HTTP errors, extract meaningful error messages
    // Server error pages often return HTML instead of JSON, so we need to detect this
    let errorMessage = `HTTP ${res.status}: ${res.statusText}`
    
    try {
      const errorText = await res.text()
      
      // Check if the response is HTML (common for server error pages)
      // HTML responses can't be parsed as JSON and cause cryptic errors
      if (errorText && !errorText.includes('<!DOCTYPE html>')) {
        errorMessage = errorText
      }
    } catch {
      // If we can't read the error text (network issues, etc.), 
      // fall back to the HTTP status message
    }
    
    throw new Error(errorMessage)
  }
  
  return res.json() as Promise<T>
}

// --- Resources ---

export async function fetchResourcesByTypeClient(type: ResourceType, opts?: { limit?: number; offset?: number }): Promise<ResourceInstance[]> {
  const p = new URLSearchParams()
  p.set('type', type)
  if (opts?.limit) p.set('limit', String(opts.limit))
  if (opts?.offset) p.set('offset', String(opts.offset))
  return getJSON<ResourceInstance[]>(`/api/resources?${p}`)
}

export async function fetchResourceBySlugClient(type: ResourceType, slug: string): Promise<ResourceInstance | null> {
  const p = new URLSearchParams()
  p.set('type', type)
  p.set('slug', slug)
  return getJSON<ResourceInstance | null>(`/api/resources?${p}`)
}

/**
 * Fetches a resource by its reference ID with defensive error handling.
 * 
 * This function is specifically designed to handle missing related resources
 * gracefully, which is common in dynamic content systems where resources
 * may be referenced but not yet created or may have been removed.
 * 
 * Key features:
 * - Returns null for missing resources instead of throwing errors
 * - Logs warnings for debugging purposes without breaking UX
 * - Handles both 404s and other API errors gracefully
 * - Maintains type safety with consistent return type
 * 
 * @param reference - The resource reference containing type and ID
 * @returns Promise<ResourceInstance | null> - The resource or null if not found/error
 * 
 * @example
 * // Used in TemplateViewer to load related resources
 * const resources = await Promise.all(
 *   template.relatedResources.map(resource => 
 *     fetchResourceByReferenceClient(resource)
 *   )
 * )
 * // Missing resources will be null, not cause errors
 * const validResources = resources.filter(r => r !== null)
 * 
 * @example
 * // Safe to use without try-catch
 * const resource = await fetchResourceByReferenceClient(reference)
 * if (resource) {
 *   // Resource exists and is valid
 *   console.log(resource.title)
 * } else {
 *   // Resource is missing - handle gracefully
 *   console.log('Resource not available')
 * }
 */
export async function fetchResourceByReferenceClient(reference: RelatedResource): Promise<ResourceInstance | null> {
  // Validate input parameters - return null for invalid references
  if (!reference?.id || !reference?.resourceType) {
    return null
  }
  
  try {
    // Attempt to fetch the resource using the enhanced getJSON function
    // This will return null for 404s and throw for other errors
    const result = await getJSON<ResourceInstance | null>(
      `/api/resources/reference?type=${encodeURIComponent(reference.resourceType)}&id=${encodeURIComponent(reference.id)}`
    )
    return result
  } catch (error) {
    // Additional safety net: catch any remaining errors and return null
    // This ensures components never crash due to API failures
    // Log warnings for debugging while maintaining good UX
    clientLogger.warn('Failed to fetch resource', { component: 'resources/api/client', resourceType: reference.resourceType, resourceId: reference.id, error: error instanceof Error ? error.message : String(error) })
    return null
  }
}

export async function fetchFeaturedResourcesClient(opts?: { limit?: number; offset?: number }): Promise<ResourceInstance[]> {
  const p = new URLSearchParams()
  p.set('featured', 'true')
  if (opts?.limit) p.set('limit', String(opts.limit))
  if (opts?.offset) p.set('offset', String(opts.offset))
  return getJSON<ResourceInstance[]>(`/api/resources?${p}`)
}

export async function getAllResourcesClient(): Promise<ResourceInstance[]> {
  // Fetch all resource types and combine them
  const [tools, guides, templates, glossaryTerms] = await Promise.all([
    fetchResourcesByTypeClient('tool'),
    fetchResourcesByTypeClient('guide'),
    fetchResourcesByTypeClient('template'),
    fetchResourcesByTypeClient('glossary-term')
  ])
  
  return [...tools, ...guides, ...templates, ...glossaryTerms]
}

// Individual resource type functions - using the unified /api/resources endpoint
export async function fetchCalculatorsClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('tool')
}

export async function fetchCalculatorBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('tool', slug)
}

export async function fetchAnalyzersClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('tool')
}

export async function fetchAnalyzerBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('tool', slug)
}

export async function fetchQuizzesClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('tool')
}

export async function fetchQuizBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('tool', slug)
}

export async function getAllGeneratorsClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('tool')
}

export async function getGeneratorBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('tool', slug)
}

export async function fetchGuidesClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('guide')
}

export async function fetchGuideBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('guide', slug)
}

export async function fetchTemplatesClient(): Promise<ResourceInstance[]> {
  return fetchResourcesByTypeClient('template')
}

export async function fetchTemplateBySlugClient(slug: string): Promise<ResourceInstance | null> {
  return fetchResourceBySlugClient('template', slug)
}

// --- Glossary ---

/**
 * @fileoverview Enhanced Glossary API Client Functions
 * 
 * This module provides comprehensive client-side access to the glossary API with support for:
 * 
 * ## Core Functions:
 * - `fetchGlossaryDataClient()` - Get complete glossary as slug-keyed record
 * - `fetchGlossaryTermClient(slug)` - Get single term by slug (throws if not found)
 * - `fetchGlossaryTermBySlugClient(slug)` - Get single term by slug (returns null if not found)
 * - `fetchGlossaryTermsClient(options)` - Get paginated terms with filtering/search
 * - `fetchFeaturedGlossaryClient(maxPerCategory)` - Get featured terms with category counts
 * - `fetchGlossaryCountsClient()` - Get term counts by category
 * 
 * ## Specialized Search Functions:
 * - `fetchGlossaryAutocompleteClient(query, limit)` - Optimized for typeahead/autocomplete
 * - `fetchGlossaryFuzzySearchClient(query, options)` - Fuzzy search with related term expansion
 * - `fetchGlossaryExactSearchClient(query, options)` - Exact string matching
 * - `fetchGlossaryTermsByAcronymClient(acronym, options)` - Search by acronym
 * - `fetchGlossaryTermsWithCustomSearchClient(query, searchFields, options)` - Custom field search
 * 
 * ## Category & Filtering Functions:
 * - `fetchGlossaryTermsByCategoryClient(category, options)` - Filter by category
 * - `fetchAllGlossaryTermsByCategoryClient(category, fields)` - Get all terms in category
 * - `fetchRelatedGlossaryTermsClient(termSlug, options)` - Find related terms
 * 
 * ## Performance Optimization Functions:
 * - `fetchGlossaryTermsWithFieldsClient(fields, options)` - Field projection for performance
 * - `fetchGlossaryTermsPaginatedClient(options, pageSize)` - Enhanced pagination with metadata
 * 
 * ## Advanced Features Supported:
 * - **Search Types**: fuzzy (default), exact, prefix, slug
 * - **Field Projection**: Request only specific fields to reduce payload size
 * - **Custom Search Fields**: Control which fields are searched
 * - **Related Term Expansion**: Include related terms in search results
 * - **Pagination**: Built-in pagination with metadata
 * - **Category Filtering**: Filter by specific categories
 * - **Performance Optimization**: Field projection and result limiting
 * 
 * ## Usage Examples:
 * 
 * ```typescript
 * // Basic usage
 * const term = await fetchGlossaryTermBySlugClient('ctr')
 * const allTerms = await fetchGlossaryTermsClient()
 * 
 * // Advanced search
 * const results = await fetchGlossaryFuzzySearchClient('conversion rate', {
 *   expandRelated: true,
 *   limit: 20,
 *   fields: ['slug', 'title', 'definition']
 * })
 * 
 * // Autocomplete
 * const suggestions = await fetchGlossaryAutocompleteClient('click', 5)
 * 
 * // Category filtering with pagination
 * const metrics = await fetchGlossaryTermsByCategoryClient('metrics', {
 *   limit: 10,
 *   offset: 20
 * })
 * 
 * // Performance-optimized field projection
 * const lightTerms = await fetchGlossaryTermsWithFieldsClient(
 *   ['slug', 'title', 'category'],
 *   { limit: 100 }
 * )
 * ```
 */

/**
 * Search match types supported by the glossary API
 */
export type GlossarySearchMatch = 'fuzzy' | 'exact' | 'prefix' | 'slug'

/**
 * Fields that can be searched in glossary terms
 */
export type GlossarySearchField = 'title' | 'shortDescription' | 'definition' | 'slug' | 'category' | 'acronym' | 'relatedTitles'

/**
 * Fields that can be projected from glossary terms
 */
export type GlossaryField = keyof GlossaryTerm

/**
 * Pagination metadata returned by paginated glossary queries
 */
export interface GlossaryPagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

/**
 * Response structure for paginated glossary queries
 */
export interface GlossaryPaginatedResponse<T = GlossaryTerm> {
  data: T[]
  pagination: GlossaryPagination
}

/**
 * Options for glossary search and filtering
 */
export interface GlossarySearchOptions {
  /** Search query to filter terms */
  q?: string
  /** Category to filter by */
  category?: string
  /** Specific fields to include in response (field projection) */
  fields?: GlossaryField[]
  /** Maximum number of results (1-100, default: 50) */
  limit?: number
  /** Number of results to skip for pagination (default: 0) */
  offset?: number
  /** Search match type (default: 'fuzzy') */
  match?: GlossarySearchMatch
  /** Include related terms in search (default: false) */
  expandRelated?: boolean
  /** Fields to search in (default: title,shortDescription,definition,slug) */
  searchFields?: GlossarySearchField[]
}

/**
 * Fetches the complete glossary as a record keyed by slug
 * 
 * @returns Promise<Record<string, GlossaryTerm>> - Object mapping slugs to terms
 * 
 * @example
 * const glossary = await fetchGlossaryDataClient()
 * const ctrTerm = glossary['ctr']
 */
export async function fetchGlossaryDataClient(): Promise<Record<string, GlossaryTerm>> {
  return getJSON<Record<string, GlossaryTerm>>('/api/glossary?record=true')
}

/**
 * Fetches a specific glossary term by its slug
 * 
 * @param slug - The slug to look up
 * @returns Promise<GlossaryTerm> - The matching term
 * @throws Error if term not found
 * 
 * @example
 * const term = await fetchGlossaryTermClient('ctr')
 */
export async function fetchGlossaryTermClient(slug: string): Promise<GlossaryTerm> {
  return getJSON<GlossaryTerm>(`/api/glossary?slug=${encodeURIComponent(slug)}`)
}

/**
 * Fetches a specific glossary term by its slug (nullable version)
 * 
 * @param slug - The slug to look up
 * @returns Promise<GlossaryTerm | null> - The matching term or null if not found
 * 
 * @example
 * const term = await fetchGlossaryTermBySlugClient('ctr')
 * if (term) {
 *   console.log(term.title)
 * }
 */
export async function fetchGlossaryTermBySlugClient(slug: string): Promise<GlossaryTerm | null> {
  try {
    return await getJSON<GlossaryTerm>(`/api/glossary?slug=${encodeURIComponent(slug)}`)
  } catch (error) {
    // If the API returns 404, return null instead of throwing
    if (error instanceof Error && error.message.includes('404')) {
      return null
    }
    throw error
  }
}

/**
 * Fetches all glossary terms with optional filtering and pagination
 * 
 * @param options - Search and filtering options
 * @returns Promise<GlossaryPaginatedResponse> - Paginated results with metadata
 * 
 * @example
 * // Get all terms
 * const allTerms = await fetchGlossaryTermsClient()
 * 
 * // Search for terms containing "click"
 * const searchResults = await fetchGlossaryTermsClient({ q: 'click' })
 * 
 * // Get only specific fields for performance
 * const lightTerms = await fetchGlossaryTermsClient({ 
 *   fields: ['slug', 'title', 'category'],
 *   limit: 20
 * })
 * 
 * // Filter by category with pagination
 * const metricTerms = await fetchGlossaryTermsClient({
 *   category: 'metrics',
 *   limit: 10,
 *   offset: 20
 * })
 */
export async function fetchGlossaryTermsClient(options?: GlossarySearchOptions): Promise<GlossaryPaginatedResponse> {
  const params = new URLSearchParams()
  
  if (options?.q) params.set('q', options.q)
  if (options?.category) params.set('category', options.category)
  if (options?.limit) params.set('limit', String(Math.min(Math.max(options.limit, 1), 100)))
  if (options?.offset) params.set('offset', String(Math.max(options.offset, 0)))
  if (options?.fields?.length) params.set('fields', options.fields.join(','))
  if (options?.match) params.set('match', options.match)
  if (options?.expandRelated) params.set('expandRelated', 'true')
  if (options?.searchFields?.length) params.set('searchFields', options.searchFields.join(','))
  
  return getJSON<GlossaryPaginatedResponse>(`/api/glossary?${params}`)
}

/**
 * Fetches featured glossary terms with category counts
 * 
 * @param maxPerCategory - Maximum number of featured terms per category (default: 1)
 * @returns Promise<{featured: GlossaryTerm[], counts: Record<string, number>}> - Featured terms and category counts
 * 
 * @example
 * // Get one featured term per category
 * const { featured, counts } = await fetchFeaturedGlossaryClient()
 * 
 * // Get up to 3 featured terms per category
 * const { featured } = await fetchFeaturedGlossaryClient(3)
 */
export async function fetchFeaturedGlossaryClient(maxPerCategory = 1): Promise<{ featured: GlossaryTerm[]; counts: Record<string, number> }> {
  const params = new URLSearchParams()
  params.set('featured', 'true')
  params.set('maxPerCategory', String(Math.max(maxPerCategory, 1)))
  
  return getJSON<{ featured: GlossaryTerm[]; counts: Record<string, number> }>(`/api/glossary?${params}`)
}

/**
 * Fetches glossary term counts by category
 * 
 * @returns Promise<Record<string, number>> - Object mapping category names to counts
 * 
 * @example
 * const counts = await fetchGlossaryCountsClient()
 * console.log(`There are ${counts.metrics} metric terms`)
 */
export async function fetchGlossaryCountsClient(): Promise<Record<string, number>> {
  const result = await getJSON<{ counts: Record<string, number> }>('/api/glossary?counts=true')
  return result.counts
}

/**
 * Fetches glossary terms filtered by category
 * 
 * @param category - Category to filter by
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Paginated results for the category
 * 
 * @example
 * // Get all metric terms
 * const metrics = await fetchGlossaryTermsByCategoryClient('metrics')
 * 
 * // Get chart terms with search
 * const charts = await fetchGlossaryTermsByCategoryClient('charts', { q: 'bar' })
 */
export async function fetchGlossaryTermsByCategoryClient(
  category: string, 
  options?: Omit<GlossarySearchOptions, 'category'>
): Promise<GlossaryPaginatedResponse> {
  return fetchGlossaryTermsClient({ ...options, category })
}

/**
 * Performs autocomplete search for glossary terms (optimized for typeahead)
 * 
 * @param query - Search query
 * @param limit - Maximum number of results (default: 10)
 * @returns Promise<Partial<GlossaryTerm>[]> - Lightweight term objects for autocomplete
 * 
 * @example
 * // Autocomplete for search input
 * const suggestions = await fetchGlossaryAutocompleteClient('click', 5)
 * // Returns: [{ slug: 'ctr', title: 'Click-Through Rate', category: 'metrics' }, ...]
 */
export async function fetchGlossaryAutocompleteClient(
  query: string, 
  limit = 10
): Promise<Partial<GlossaryTerm>[]> {
  const result = await fetchGlossaryTermsClient({
    q: query,
    match: 'prefix', // Use prefix matching for autocomplete
    fields: ['slug', 'title', 'category', 'acronym'], // Only essential fields
    limit: Math.min(limit, 20), // Cap at 20 for performance
    expandRelated: false // Don't expand related terms for autocomplete
  })
  
  return result.data
}

/**
 * Performs fuzzy search across glossary terms with related term expansion
 * 
 * @param query - Search query
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Search results with pagination
 * 
 * @example
 * // Fuzzy search with related terms
 * const results = await fetchGlossaryFuzzySearchClient('conversion rate', {
 *   expandRelated: true,
 *   limit: 20
 * })
 */
export async function fetchGlossaryFuzzySearchClient(
  query: string,
  options?: Omit<GlossarySearchOptions, 'q' | 'match'>
): Promise<GlossaryPaginatedResponse> {
  return fetchGlossaryTermsClient({
    ...options,
    q: query,
    match: 'fuzzy'
  })
}

/**
 * Performs exact search for glossary terms
 * 
 * @param query - Exact search query
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Exact match results
 * 
 * @example
 * // Exact search for specific term
 * const exact = await fetchGlossaryExactSearchClient('Click-Through Rate')
 */
export async function fetchGlossaryExactSearchClient(
  query: string,
  options?: Omit<GlossarySearchOptions, 'q' | 'match'>
): Promise<GlossaryPaginatedResponse> {
  return fetchGlossaryTermsClient({
    ...options,
    q: query,
    match: 'exact'
  })
}

/**
 * Fetches glossary terms with custom field projection for performance optimization
 * 
 * @param fields - Specific fields to include in response
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse<Partial<GlossaryTerm>>> - Results with projected fields
 * 
 * @example
 * // Get only essential fields for a large list
 * const lightTerms = await fetchGlossaryTermsWithFieldsClient(
 *   ['slug', 'title', 'category'],
 *   { limit: 100 }
 * )
 */
export async function fetchGlossaryTermsWithFieldsClient(
  fields: GlossaryField[],
  options?: Omit<GlossarySearchOptions, 'fields'>
): Promise<GlossaryPaginatedResponse<Partial<GlossaryTerm>>> {
  return fetchGlossaryTermsClient({
    ...options,
    fields
  }) as Promise<GlossaryPaginatedResponse<Partial<GlossaryTerm>>>
}

/**
 * Fetches all glossary terms in a specific category (non-paginated)
 * 
 * @param category - Category to filter by
 * @param fields - Optional field projection for performance
 * @returns Promise<GlossaryTerm[]> - All terms in the category
 * 
 * @example
 * // Get all metric terms
 * const allMetrics = await fetchAllGlossaryTermsByCategoryClient('metrics')
 * 
 * // Get only essential fields for charts
 * const chartTitles = await fetchAllGlossaryTermsByCategoryClient('charts', ['slug', 'title'])
 */
export async function fetchAllGlossaryTermsByCategoryClient(
  category: string,
  fields?: GlossaryField[]
): Promise<GlossaryTerm[]> {
  const result = await fetchGlossaryTermsClient({
    category,
    fields,
    limit: 100 // Get all terms (API max is 100)
  })
  
  return result.data
}

/**
 * Searches for glossary terms by acronym
 * 
 * @param acronym - Acronym to search for
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Terms matching the acronym
 * 
 * @example
 * // Find terms with specific acronym
 * const ctrTerms = await fetchGlossaryTermsByAcronymClient('CTR')
 */
export async function fetchGlossaryTermsByAcronymClient(
  acronym: string,
  options?: Omit<GlossarySearchOptions, 'q' | 'searchFields'>
): Promise<GlossaryPaginatedResponse> {
  return fetchGlossaryTermsClient({
    ...options,
    q: acronym,
    searchFields: ['acronym', 'title'], // Search in acronym and title fields
    match: 'exact' // Use exact matching for acronyms
  })
}

/**
 * Fetches related terms for a given glossary term
 * 
 * @param termSlug - Slug of the term to find related terms for
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Related terms
 * 
 * @example
 * // Get terms related to CTR
 * const relatedTerms = await fetchRelatedGlossaryTermsClient('ctr')
 */
export async function fetchRelatedGlossaryTermsClient(
  termSlug: string,
  options?: Omit<GlossarySearchOptions, 'expandRelated'>
): Promise<GlossaryPaginatedResponse> {
  // First get the term to find its related terms
  const term = await fetchGlossaryTermBySlugClient(termSlug)
  if (!term?.relatedTerms?.length) {
    return { data: [], pagination: { total: 0, limit: 50, offset: 0, hasMore: false } }
  }
  
  // Search for the related terms by their titles
  const relatedTitles = term.relatedTerms.map(rt => rt.title).join(' ')
  return fetchGlossaryTermsClient({
    ...options,
    q: relatedTitles,
    searchFields: ['title'],
    match: 'fuzzy',
    expandRelated: true
  })
}

/**
 * Fetches glossary terms with custom search field configuration
 * 
 * @param query - Search query
 * @param searchFields - Specific fields to search in
 * @param options - Additional search options
 * @returns Promise<GlossaryPaginatedResponse> - Search results
 * 
 * @example
 * // Search only in titles and definitions
 * const results = await fetchGlossaryTermsWithCustomSearchClient(
 *   'conversion',
 *   ['title', 'definition']
 * )
 */
export async function fetchGlossaryTermsWithCustomSearchClient(
  query: string,
  searchFields: GlossarySearchField[],
  options?: Omit<GlossarySearchOptions, 'q' | 'searchFields'>
): Promise<GlossaryPaginatedResponse> {
  return fetchGlossaryTermsClient({
    ...options,
    q: query,
    searchFields
  })
}

/**
 * Utility function to get paginated results with automatic pagination handling
 * 
 * @param options - Search options
 * @param pageSize - Number of items per page (default: 50)
 * @returns Promise<{terms: GlossaryTerm[], totalPages: number, currentPage: number}> - Paginated results
 * 
 * @example
 * // Get first page of search results
 * const { terms, totalPages, currentPage } = await fetchGlossaryTermsPaginatedClient({
 *   q: 'click',
 *   category: 'metrics'
 * }, 20)
 */
export async function fetchGlossaryTermsPaginatedClient(
  options: GlossarySearchOptions,
  pageSize = 50
): Promise<{
  terms: GlossaryTerm[]
  totalPages: number
  currentPage: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}> {
  const result = await fetchGlossaryTermsClient({
    ...options,
    limit: Math.min(pageSize, 100)
  })
  
  const currentPage = Math.floor((options.offset ?? 0) / pageSize) + 1
  const totalPages = Math.ceil(result.pagination.total / pageSize)
  
  return {
    terms: result.data,
    totalPages,
    currentPage,
    hasNextPage: result.pagination.hasMore,
    hasPreviousPage: (options.offset ?? 0) > 0
  }
}
